import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type { SaleWithRelations } from '@borradh-workspace/api-client/types';
import {
  type ChargeSavedCardRequest,
  saleWithRelationsSchema,
  saleWithSavedCardsSchema,
} from '@borradh-workspace/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { syncSaleCaches } from '../sales-cache';

export const useSavedCardsForSale = (
  saleId: string,
  leadId: string | null,
  enabled: boolean
) => {
  const query = useQuery({
    queryKey: queryKeys.sales.savedCards(saleId, leadId),
    queryFn: () =>
      apiClient.get(`sales/${saleId}?includeSavedCards=true`, {
        schema: saleWithSavedCardsSchema,
      }),
    enabled: !!saleId && enabled,
    staleTime: 30_000,
  });
  return {
    cards: query.data?.savedCards ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
};

export const useChargeSavedCard = (saleId: string) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: ChargeSavedCardRequest) =>
      apiClient.post<SaleWithRelations>(
        `sales/${saleId}/payments`,
        { ...input, method: 'saved_card' },
        {
          schema: saleWithRelationsSchema,
        }
      ),
    onSuccess: (sale) => {
      syncSaleCaches(queryClient, sale);
      toast.success('Saved card charged');
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Card charge failed'),
  });
  return {
    chargeSavedCardAsync: mutation.mutateAsync,
    isCharging: mutation.isPending,
  };
};
